import { useEffect, useRef } from 'react';

const toHiragana = (value: string): string =>
  value.replace(/[\u30A1-\u30F6]/g, (char) =>
    String.fromCharCode(char.charCodeAt(0) - 0x60)
  );

/** ひらがな・カタカナ・長音のみ抽出（漢字は捨てる） */
const extractKana = (value: string): string =>
  toHiragana(value).replace(/[^\u3040-\u309F\u30FC]/g, '');

/**
 * 店舗名の IME 入力からふりがな欄へ読みを自動反映する。
 * 漢字変換後のデータは無視し、かなのみ反映する。
 * ふりがなを手編集した場合は自動反映を止める。
 */
export const useAutoFurigana = (
  name: string,
  furigana: string,
  onFuriganaChange: (furigana: string) => void,
  enabled: boolean
) => {
  const nameRef = useRef<HTMLInputElement | null>(null);
  const furiganaTouchedRef = useRef(furigana.trim() !== '');
  const furiganaRef = useRef(furigana);
  const baseFuriganaRef = useRef('');
  const activeReadingRef = useRef('');
  const isComposingRef = useRef(false);
  const onFuriganaChangeRef = useRef(onFuriganaChange);

  useEffect(() => {
    onFuriganaChangeRef.current = onFuriganaChange;
  }, [onFuriganaChange]);

  useEffect(() => {
    furiganaRef.current = furigana;
  }, [furigana]);

  useEffect(() => {
    if (!enabled) {
      return;
    }

    const nameEl = nameRef.current;
    if (!nameEl) {
      return;
    }

    const handleCompositionStart = () => {
      isComposingRef.current = true;
      if (!furiganaTouchedRef.current) {
        baseFuriganaRef.current = furiganaRef.current;
        activeReadingRef.current = '';
      }
    };

    const handleCompositionUpdate = (event: CompositionEvent) => {
      if (furiganaTouchedRef.current) {
        return;
      }
      const reading = extractKana(event.data || '');
      // 漢字変換中は data に漢字が入るため、かなが取れない更新は無視する
      if (reading === '') {
        return;
      }
      activeReadingRef.current = reading;
      onFuriganaChangeRef.current(baseFuriganaRef.current + activeReadingRef.current);
    };

    const handleCompositionEnd = () => {
      isComposingRef.current = false;
      if (furiganaTouchedRef.current) {
        return;
      }
      // compositionend の data は変換後の漢字になりやすいので、最後のかな読みを使う
      if (activeReadingRef.current !== '') {
        onFuriganaChangeRef.current(baseFuriganaRef.current + activeReadingRef.current);
      }
      activeReadingRef.current = '';
    };

    nameEl.addEventListener('compositionstart', handleCompositionStart);
    nameEl.addEventListener('compositionupdate', handleCompositionUpdate);
    nameEl.addEventListener('compositionend', handleCompositionEnd);

    return () => {
      nameEl.removeEventListener('compositionstart', handleCompositionStart);
      nameEl.removeEventListener('compositionupdate', handleCompositionUpdate);
      nameEl.removeEventListener('compositionend', handleCompositionEnd);
    };
  }, [enabled]);

  useEffect(() => {
    if (!enabled || furiganaTouchedRef.current || isComposingRef.current) {
      return;
    }
    if (name.trim() === '' && furigana !== '') {
      onFuriganaChangeRef.current('');
    }
  }, [enabled, name, furigana]);

  const markFuriganaTouched = () => {
    furiganaTouchedRef.current = true;
  };

  return { nameRef, markFuriganaTouched };
};
